import { observable, action, makeObservable } from 'mobx'
import api from '../api'
import { selectionStore, dataStore } from '../stores/UIStore'
import { getDefaultGrayColor } from '../utils'
import { selectionTypes } from 'constants.js'
import _ from 'lodash'

class PointOfInterest {
  // Google data
  @observable placePredictions = []
  @observable queryPredictions = []
  @observable placeResults = []
  @observable temporaryMarkers = []

  // UI state
  @observable openMenuId = null
  @observable openMenuSource = null // 'results' | 'bucket'
  @observable menuStepById = observable.map() // itemId -> 'main' | 'categories'
  @observable viewFilter = 'all' // 'all' | 'general' | 'competitor' | 'ally'
  @observable disabledRowsById = observable.map()
  @observable hoveredMoveId = null

  // id -> observable array of buckets this marker belongs to (e.g. ['general', 'ally'])
  @observable bucketById = observable.map()
  // id -> currently selected (pending) bucket via the "⋮" menu
  @observable pendingBucketById = observable.map() // 'general' | 'ally' | 'competitor'

  // Baseline state - buckets as persisted in the database (for Reset functionality)
  @observable sharedBucketsById = observable.map()

  // Track items that have been explicitly removed from buckets (to override baseline)
  @observable removedFromBucketById = observable.map() // id -> array of removed bucket types

  // non-exclusive lists (a marker can appear in multiple)
  @observable general = []
  @observable allies = []
  @observable competitors = []
  @observable hasBucketChanges = false
  @observable isLimitExceeded = false
  @observable limitExceededMessage = ''
  @observable loading = false

  // Track the current target group for shared bucket state
  @observable currentTargetGroupId = null

  constructor () {
    makeObservable(this)
  }

  // ---------- UI helpers ----------
  @action.bound setViewFilter (f) {
    if (f === 'all' || f === 'general' || f === 'competitor' || f === 'ally') {
      this.viewFilter = f
    }
  }

  @action.bound toggleMenu (id) {
    this.openMenuId = this.openMenuId === id ? null : id
  }

  /** Store the selection from the menu; do NOT close the menu */
  @action.bound setPendingBucket (id, bucket) {
    if (!id || !bucket) return
    this.pendingBucketById.set(id, bucket)
    // (intentionally do not touch this.openMenuId)
  }

  /** Click on + : add to the previously chosen bucket (or General), then close the menu */
  @action.bound addPendingToBucket (id) {
    if (!id) return
    const selected = this.pendingBucketById.get(id) || 'general'

    // Check if this marker had ever been explicitly removed from buckets in this POI session.
    // We capture this before calling addToBucket, because addToBucket may clear the entry.
    const hadRemovals = !!(this.removedFromBucketById && typeof this.removedFromBucketById.get === 'function' &&
      this.removedFromBucketById.get(id) && this.removedFromBucketById.get(id).length > 0)

    this.addToBucket(id, selected)

    // If this marker was previously "Removed" (had explicit removals)
    // and the user is now saving it back to a bucket via '+',
    // treat it as a fully promoted saved marker: remove it from
    // the POI search results so it no longer appears in the Results card.
    if (hadRemovals) {
      // Remove from temporaryMarkers (map markers used by Results)
      if (Array.isArray(this.temporaryMarkers)) {
        this.temporaryMarkers = this.temporaryMarkers.filter(m => m && m.id !== id)
      }
      // Remove from placeResults (the flat search results backing list)
      if (Array.isArray(this.placeResults)) {
        this.placeResults = this.placeResults.filter(pr =>
          pr &&
          pr.id !== id &&
          pr.place_id !== id
        )
      }
      // Clear disabled/removed state for this id
      if (this.disabledRowsById && typeof this.disabledRowsById.delete === 'function') {
        this.disabledRowsById.delete(id)
      }
      if (this.removedFromBucketById && typeof this.removedFromBucketById.delete === 'function') {
        this.removedFromBucketById.delete(id)
      }
    }

    // Close the menu after adding to make the UX clearer
    this.openMenuId = null
  }

  /** Actual add-to-bucket operation (idempotent) */
  @action.bound addToBucket (id, bucket) {
    if (!id || !bucket) return
    const marker = this.temporaryMarkers.find(x => x.id === id)
    if (!marker) return

    // Reactive array of buckets for this id
    let list = this.bucketById.get(id)
    if (!list) {
      list = observable.array([])
      this.bucketById.set(id, list)
    }
    let membershipChanged = false
    if (list.indexOf(bucket) === -1) {
      list.push(bucket)
      membershipChanged = true
    }

    // Remove from removedFromBucketById if it was there (un-remove it)
    const removedList = this.removedFromBucketById.get(id)
    if (removedList) {
      const idx = removedList.indexOf(bucket)
      if (idx !== -1) {
        removedList.splice(idx, 1)
        membershipChanged = true
      }
      // Clean up empty removed list
      if (removedList.length === 0) {
        this.removedFromBucketById.delete(id)
      }
    }

    // Insert into the corresponding list if it is not already there
    const pushUnique = (arr, item) => {
      const idx = arr.findIndex(x => x.id === item.id)
      if (idx === -1) arr.push(item)
    }
    if (bucket === 'general') pushUnique(this.general, marker)
    else if (bucket === 'ally') pushUnique(this.allies, marker)
    else if (bucket === 'competitor') pushUnique(this.competitors, marker)

    this.disabledRowsById.set(id, true)

    if (membershipChanged) {
      this.markBucketChange()
    }
  }

  /** Remove from a specific bucket; if it's not in any bucket anymore, remove its id from maps */
  @action.bound removeFromBucket (id, bucket) {
    if (!id || !bucket) return

    // Remove from the array for that bucket
    const removeFrom = (arr) => {
      const i = arr.findIndex(x => x.id === id)
      if (i > -1) arr.splice(i, 1)
    }
    if (bucket === 'general') removeFrom(this.general)
    else if (bucket === 'ally') removeFrom(this.allies)
    else if (bucket === 'competitor') removeFrom(this.competitors)

    // Update membership
    const list = this.bucketById.get(id)
    let membershipChanged = false
    if (list) {
      const li = list.indexOf(bucket)
      if (li !== -1) {
        list.splice(li, 1)
        membershipChanged = true
      }
      if (list.length === 0) {
        this.bucketById.delete(id)
        this.pendingBucketById.delete(id)
        this.disabledRowsById.delete(id)
      }
    }

    // Track this removal to override baseline state
    let removedList = this.removedFromBucketById.get(id)
    if (!removedList) {
      removedList = observable.array([])
      this.removedFromBucketById.set(id, removedList)
    }
    if (removedList.indexOf(bucket) === -1) {
      removedList.push(bucket)
      membershipChanged = true
    }

    if (membershipChanged) {
      this.markBucketChange()
    }
  }

  /** Remove a marker from all bucket state by matching lat/lng with a saved marker.
   * When called from tools menu deletion, also removes from temporaryMarkers. */
  @action.bound
  removeMarkerFromAllBucketsBySavedMarker (savedMarker, removeFromTemporaryMarkers = false) {
    if (!savedMarker || !savedMarker.position) return

    const lat = savedMarker.position.latitude
    const lng = savedMarker.position.longitude
    const EPS = 1e-4

    // Find matching temporary marker by lat/lng
    const tempMarker = this.temporaryMarkers.find(m => {
      const mLat = m.position && m.position.latLng && m.position.latLng.lat
      const mLng = m.position && m.position.latLng && m.position.latLng.lng
      return Math.abs(mLat - lat) < EPS && Math.abs(mLng - lng) < EPS
    })

    if (tempMarker) {
      // Remove from all bucket arrays
      const removeFromArr = (arr) => {
        const idx = arr.findIndex(x => x.id === tempMarker.id)
        if (idx > -1) arr.splice(idx, 1)
      }
      removeFromArr(this.general)
      removeFromArr(this.allies)
      removeFromArr(this.competitors)

      // Clear from all maps
      this.bucketById.delete(tempMarker.id)
      this.sharedBucketsById.delete(tempMarker.id)
      this.pendingBucketById.delete(tempMarker.id)
      this.disabledRowsById.delete(tempMarker.id)
      this.removedFromBucketById.delete(tempMarker.id)
      // If removing from tools menu, also remove from temporaryMarkers
      // so it disappears from POI menu search results
      if (removeFromTemporaryMarkers) {
        const tempIndex = this.temporaryMarkers.indexOf(tempMarker)
        if (tempIndex > -1) {
          this.temporaryMarkers.splice(tempIndex, 1)
          // Force re-render
          this.temporaryMarkers = this.temporaryMarkers.slice()
        }
      }
    }
  }

  // ---------- Google API ----------
  @action.bound
  async queryAndSetPredictions (payload) {
    try {
      const response = await api.getGoogleAutocomplete(payload)
      const data = await response.json()
      if (!data || !data.suggestions) return
      this.placePredictions = data.suggestions.map(x => x.placePrediction).filter(Boolean)
      this.queryPredictions = data.suggestions.map(x => x.queryPrediction).filter(Boolean)
    } catch (e) {
      console.error('Autocomplete error:', e)
      this.placePredictions = []
      this.queryPredictions = []
    }
  }

  /**
   * Ensure that a given marker has address, type and icon_url populated by
   * fetching Google Places data for its exact location.
   * This is used for saved markers whose original POI metadata was not
   * persisted, so tooltips can still show full information regardless of
   * which polygon is currently selected.
   */
  @action.bound
  async ensureMarkerDetails (marker) {
    try {
      console.log('markerrrrrr', marker)
      if (!marker || !marker.position || !marker.position.latLng) return

      // PREFERRED: Use place_id if available (more efficient and accurate)
      // Marker from backend should have place_id field
      // Check both marker.place_id (from SavedMarker) and marker.placeId (from temporary markers)
      const rawPlaceId = marker.place_id || marker.placeId
      const placeId = typeof rawPlaceId === 'string' && rawPlaceId.trim() && rawPlaceId !== 'null' && rawPlaceId !== 'undefined'
        ? rawPlaceId.trim()
        : null

      // If no valid place_id, this is not a Google Places place - skip details fetch
      if (!placeId) {
        return
      }

      console.log('[POI] ensureMarkerDetails - marker:', {
        id: marker.id,
        place_id: marker.place_id,
        placeId: marker.placeId,
        hasPlaceId: !!placeId,
        placeIdValue: placeId
      })

      let place = null

      if (placeId) {
        // Use place details API (more efficient - direct lookup by ID)
        try {
          console.log('[POI] Calling place details API for place_id:', placeId)
          const response = await api.getGooglePlaceDetails(placeId)
          if (response.ok) {
            const data = await response.json()
            place = data
            console.log('[POI] Place details API success')
          } else {
            // If place details fails, don't fall back to nearby search
            // Manually created markers don't have place_id, so we shouldn't fetch details
            const errorData = await response.json().catch(() => ({}))
            console.warn('Place details API failed, skipping:', errorData)
            return
          }
        } catch (err) {
          console.warn('Place details API error, skipping:', err)
          return
        }
      } else {
        return
      }

      // Only process if we got place data from place details API
      if (!place || !place.location) return

      // Merge details into the existing marker instance (SavedMarker is observable)
      const formattedAddress = place.formattedAddress || null
      if (!marker.address && formattedAddress) {
        marker.address = formattedAddress
      }
      // Keep description as-is; address is preferred for tooltip

      if (!marker.icon_url && place.iconMaskBaseUri) {
        marker.icon_url = place.iconMaskBaseUri
        // Also update matching temporary marker if it exists
        // Find by savedMarkerId or by matching id pattern
        if (this.temporaryMarkers && this.temporaryMarkers.length > 0) {
          const savedMarkerId = marker.savedMarkerId || (typeof marker.id === 'number' ? marker.id : null)
          const markerId = savedMarkerId ? `saved_${savedMarkerId}` : marker.id
          // Find matching temporary marker by id or savedMarkerId
          const tempMarkerIndex = this.temporaryMarkers.findIndex(m =>
            m.id === markerId ||
            m.savedMarkerId === savedMarkerId ||
            (savedMarkerId && m.id === `saved_${savedMarkerId}`)
          )
          if (tempMarkerIndex >= 0) {
            // Update the temporary marker - replace the object to trigger MobX reaction
            const updatedMarker = {
              ...this.temporaryMarkers[tempMarkerIndex],
              icon_url: place.iconMaskBaseUri
            }
            this.temporaryMarkers[tempMarkerIndex] = updatedMarker
          }
        }
      }

      // Prefer first type / primaryType for display
      const primaryType =
        (Array.isArray(place.types) && place.types.length && place.types[0]) ||
        place.primaryType ||
        ''

      if (primaryType) {
        // Some markers may use a 'type' field, others 'types' as string/array
        if (!marker.type) {
          marker.type = primaryType
        }
        if (!marker.types) {
          marker.types = Array.isArray(place.types)
            ? place.types.join(',')
            : (marker.types || primaryType)
        }
      }

      // Store placeId in marker for future use (avoid nearby search next time)
      if (place.id && !marker.placeId && !marker.place_id) {
        marker.placeId = place.id
      }
    } catch (e) {
      // Fail silently – tooltip will just show whatever data is already available
      console.warn('ensureMarkerDetails error:', e)
    }
  }

  @action.bound
  async queryAndSetPlaceResults (payload) {
    this.loading = true
    try {
      const response = await api.getGoogleNearbyPlaces(payload)

      // Handle limit exceeded or other errors
      if (!response.ok) {
        const errorData = await response.json()
        if (response.status === 403 && errorData.error === 'limit_exceeded') {
          // Limit exceeded - error already shown in UIStore
          this.placeResults = []
          this.replaceTemporaryMarkersPreservingClassified([])
          this.isLimitExceeded = true
          this.limitExceededMessage = errorData.message || ''
          this.loading = false
          return
        }
        // Other errors
        this.placeResults = []
        this.replaceTemporaryMarkersPreservingClassified([])
        return
      }
      this.isLimitExceeded = false
      this.limitExceededMessage = ''

      const data = await response.json()
      if (!data || !Array.isArray(data.places)) {
        this.placeResults = []
        // Empty/error search must not drop items already classified into buckets
        this.replaceTemporaryMarkersPreservingClassified([])
        return
      }
      this.placeResults = this.serializePlaces(data.places)

      // Sync buckets from saved markers in the target group (for persisted state)
      const targetGroup = selectionStore && selectionStore.data && selectionStore.data.group
      if (targetGroup) {
        // Reload saved markers to get latest flags, then sync
        await targetGroup.loadSavedMarkers()

        // Get the selected shape's geometry for filtering
        const selectedShape = selectionStore && selectionStore.item
        const shapeGeoJSON = selectedShape && selectedShape.geoJSON

        // Add saved markers that are inside this shape to temporaryMarkers
        // This ensures saved markers appear even if Google doesn't return them
        if (targetGroup.savedMarkers && targetGroup.savedMarkers.length > 0) {
          const EPS = 1e-4
          const savedMarkersTempFormat = targetGroup.savedMarkers
            .filter(sm => {
              if (!sm.position || sm.position.latitude == null || sm.position.longitude == null) return false
              // Check if marker is inside the selected shape
              if (shapeGeoJSON) {
                return this.pointInGeoJSON(sm.position.latitude, sm.position.longitude, shapeGeoJSON)
              }
              return true
            })
            .map(sm => ({
              id: `saved_${sm.id}`,
              savedMarkerId: sm.id,
              position: { latLng: { lat: sm.position.latitude, lng: sm.position.longitude } },
              name: sm.name || '',
              address: sm.description || '',
              // Use backend `types` string for type display
              type: (sm.types && typeof sm.types === 'string') ? sm.types : '',
              types: sm.types || '',
              getColor: sm.color_id || 1,
              color_id: sm.color_id || 1, // Preserve color_id for icon coloring
              icon_url: sm.icon_url || null,
              is_general: sm.is_general,
              is_ally: sm.is_ally,
              is_competitor: sm.is_competitor
            }))

          // Merge saved markers with Google markers (avoiding duplicates)
          const currentMarkers = this.temporaryMarkers.slice()
          for (const sm of savedMarkersTempFormat) {
            const smLat = sm.position.latLng.lat
            const smLng = sm.position.latLng.lng
            const matchingMarker = currentMarkers.find(m => {
              const mLat = m.position && m.position.latLng && m.position.latLng.lat
              const mLng = m.position && m.position.latLng && m.position.latLng.lng
              return Math.abs(mLat - smLat) < EPS && Math.abs(mLng - smLng) < EPS
            })
            if (matchingMarker) {
              // Copy flags, savedMarkerId, and color_id to the matching marker
              matchingMarker.savedMarkerId = sm.savedMarkerId
              matchingMarker.is_general = sm.is_general
              matchingMarker.is_ally = sm.is_ally
              matchingMarker.is_competitor = sm.is_competitor
              matchingMarker.color_id = sm.color_id // Preserve color_id for icon coloring
              matchingMarker.getColor = sm.getColor // Also update getColor to match
            } else {
              currentMarkers.push(sm)
            }
          }
          this.temporaryMarkers = currentMarkers
        }

        this.syncBucketsFromSavedMarkers(targetGroup)
      }
    } catch (err) {
      console.error('Error in queryAndSetPlaceResults:', err)
      this.placeResults = []
      // Empty/error search must not drop items already classified into buckets
      this.replaceTemporaryMarkersPreservingClassified([])
    } finally {
      this.loading = false
    }
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm
   * @param {number} lat - Latitude of the point
   * @param {number} lng - Longitude of the point
   * @param {Array} ring - Array of [lng, lat] coordinates forming the polygon ring
   * @returns {boolean} - True if point is inside the polygon
   */
  pointInPolygonRing (lat, lng, ring) {
    if (!ring || ring.length < 3) return false
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0]; const yi = ring[i][1]
      const xj = ring[j][0]; const yj = ring[j][1]
      const intersect = ((yi > lat) !== (yj > lat)) &&
        (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)
      if (intersect) inside = !inside
    }
    return inside
  }

  /**
   * Check if a point is inside a GeoJSON geometry (Polygon or MultiPolygon)
   * @param {number} lat - Latitude of the point
   * @param {number} lng - Longitude of the point
   * @param {Object} geoJSON - GeoJSON geometry object
   * @returns {boolean} - True if point is inside any polygon
   */
  pointInGeoJSON (lat, lng, geoJSON) {
    if (!geoJSON) return false
    if (geoJSON.type === 'Polygon' && geoJSON.coordinates) {
      // First ring is exterior, subsequent rings are holes
      const exterior = geoJSON.coordinates[0]
      if (!this.pointInPolygonRing(lat, lng, exterior)) return false
      // Check holes
      for (let h = 1; h < geoJSON.coordinates.length; h++) {
        if (this.pointInPolygonRing(lat, lng, geoJSON.coordinates[h])) return false
      }
      return true
    } else if (geoJSON.type === 'MultiPolygon' && geoJSON.coordinates) {
      for (const polygon of geoJSON.coordinates) {
        const exterior = polygon[0]
        if (this.pointInPolygonRing(lat, lng, exterior)) {
          // Check holes
          let inHole = false
          for (let h = 1; h < polygon.length; h++) {
            if (this.pointInPolygonRing(lat, lng, polygon[h])) {
              inHole = true
              break
            }
          }
          if (!inHole) return true
        }
      }
      return false
    }
    return false
  }

  /**
   * Check if a point is inside any of the provided shapes
   * @param {number} lat - Latitude of the point
   * @param {number} lng - Longitude of the point
   * @param {Array} shapes - Array of shapes with geoJSON property
   * @returns {boolean} - True if point is inside at least one shape
   */
  pointInAnyShape (lat, lng, shapes) {
    if (!shapes || shapes.length === 0) return true // No filtering if no shapes
    for (const shape of shapes) {
      if (shape.geoJSON && this.pointInGeoJSON(lat, lng, shape.geoJSON)) {
        return true
      }
    }
    return false
  }

  /** Query POI results for a target group (combines results from all shapes) */
  @action.bound
  async queryAndSetPlaceResultsForTargetGroup (payload, targetGroup, shapes = []) {
    this.loading = true
    try {
      const EPS = 1e-4
      const allGooglePlaces = []
      const seenPlaceIds = new Set()

      // Query Google Places for EACH shape separately to ensure we get results from all shapes
      // This is necessary because a single API call with a large bounding circle may not return
      // results for all shapes if they are spread apart
      if (shapes.length > 0) {
        for (const shape of shapes) {
          if (typeof shape.bounds !== 'function') continue
          const b = shape.bounds()
          if (!b || !b[0] || !b[1]) continue

          const sw = { lat: b[0][0], lng: b[0][1] }
          const ne = { lat: b[1][0], lng: b[1][1] }
          const center = {
            latitude: (sw.lat + ne.lat) / 2,
            longitude: (sw.lng + ne.lng) / 2
          }
          const radius = (() => {
            const toRad = d => d * Math.PI / 180
            const R = 6371000
            const dLat = toRad(ne.lat - center.latitude)
            const dLng = toRad(ne.lng - center.longitude)
            const la1 = toRad(center.latitude)
            const la2 = toRad(ne.lat)
            const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
            return Math.max(50, Math.ceil(2 * R * Math.asin(Math.sqrt(h))))
          })()

          const shapePayload = {
            maxResultCount: 20,
            rankPreference: 'POPULARITY',
            locationRestriction: { circle: { center, radius } }
          }

          try {
            // eslint-disable-next-line no-await-in-loop
            const response = await api.getGoogleNearbyPlaces(shapePayload)

            // Handle limit exceeded or other errors
            if (!response.ok) {
              // eslint-disable-next-line no-await-in-loop
              const errorData = await response.json()
              if (response.status === 403 && errorData.error === 'limit_exceeded') {
                // Limit exceeded - skip this shape, continue with others
                continue
              }
              // Other errors - skip this shape
              continue
            }

            // eslint-disable-next-line no-await-in-loop
            const data = await response.json()
            const places = (data && Array.isArray(data.places)) ? data.places : []

            // Filter to only include places inside THIS shape and not already seen
            for (const p of places) {
              if (!p.location || typeof p.location.latitude !== 'number' || typeof p.location.longitude !== 'number') continue
              const placeId = p.id || p.placeId || `${p.location.latitude},${p.location.longitude}`
              if (seenPlaceIds.has(placeId)) continue
              // Check if inside this specific shape
              if (shape.geoJSON && this.pointInGeoJSON(p.location.latitude, p.location.longitude, shape.geoJSON)) {
                seenPlaceIds.add(placeId)
                allGooglePlaces.push(p)
              }
            }
          } catch (shapeErr) {
            console.warn('Error querying places for shape:', shapeErr)
          }
        }
      } else {
        // No shapes - use the provided payload as fallback
        const response = await api.getGoogleNearbyPlaces(payload)

        if (!response.ok) {
          const errorData = await response.json()
          if (response.status === 403 && errorData.error === 'limit_exceeded') {
            // Limit exceeded - return empty results
            this.isLimitExceeded = true
            this.limitExceededMessage = errorData.message || ''
            return []
          }
          // Other errors - return empty results
          return []
        }
        this.isLimitExceeded = false
        this.limitExceededMessage = ''

        const data = await response.json()
        const places = (data && Array.isArray(data.places)) ? data.places : []
        for (const p of places) {
          if (p.location && typeof p.location.latitude === 'number' && typeof p.location.longitude === 'number') {
            allGooglePlaces.push(p)
          }
        }
      }

      const googleMarkers = allGooglePlaces.map(p => {
        const stableId = p.id || p.placeId || (p.location ? (`${p.location.latitude},${p.location.longitude}`) : Math.random().toString(36).slice(2))
        return {
          id: stableId,
          position: { latLng: { lat: p.location.latitude, lng: p.location.longitude } },
          name: (p.displayName && p.displayName.text) || '',
          address: p.formattedAddress || '',
          type: Array.isArray(p.types) && p.types.length ? p.types[0] : '',
          types: p.types || [],
          getColor: 7,
          icon_url: p.iconMaskBaseUri || null
        }
      })

      // Also include saved markers from the target group as temporary markers
      // This ensures they appear in bucket cards even if Google Places doesn't return them
      // For target group view, include ALL saved markers (don't filter by shape)
      const savedMarkersArr = (targetGroup && targetGroup.savedMarkers) ? targetGroup.savedMarkers.slice() : []
      const savedMarkersTempFormat = savedMarkersArr
        .filter(sm => sm.position && sm.position.latitude != null && sm.position.longitude != null)
        .map(sm => ({
          id: `saved_${sm.id}`,
          savedMarkerId: sm.id,
          position: { latLng: { lat: sm.position.latitude, lng: sm.position.longitude } },
          name: sm.name || '',
          address: sm.description || '',
          type: (sm.types && typeof sm.types === 'string') ? sm.types : '',
          types: sm.types || '',
          getColor: sm.color_id || 1,
          color_id: sm.color_id || 1,
          icon_url: sm.icon_url || null,
          is_general: sm.is_general,
          is_ally: sm.is_ally,
          is_competitor: sm.is_competitor
        }))

      // Merge: Google markers + saved markers (avoiding duplicates by lat/lng)
      // If a Google marker matches a saved marker, copy the flags and savedMarkerId to the Google marker
      const allMarkers = [...googleMarkers]
      for (const sm of savedMarkersTempFormat) {
        const smLat = sm.position.latLng.lat
        const smLng = sm.position.latLng.lng
        const matchingGoogleMarker = allMarkers.find(m => {
          const mLat = m.position.latLng.lat
          const mLng = m.position.latLng.lng
          return Math.abs(mLat - smLat) < EPS && Math.abs(mLng - smLng) < EPS
        })
        if (matchingGoogleMarker) {
          // Copy flags and savedMarkerId to the matching Google marker
          matchingGoogleMarker.savedMarkerId = sm.savedMarkerId
          matchingGoogleMarker.is_general = sm.is_general
          matchingGoogleMarker.is_ally = sm.is_ally
          matchingGoogleMarker.is_competitor = sm.is_competitor
          matchingGoogleMarker.color_id = sm.color_id
        } else {
          allMarkers.push(sm)
        }
      }

      // Keep any POIs already added to General / Comp / Ally while showing this group's markers
      this.replaceTemporaryMarkersPreservingClassified(allMarkers)
      this.placeResults = allGooglePlaces.map(p => ({
        id: p.id,
        place_id: p.id,
        name: (p.displayName && p.displayName.text) || '',
        description: '',
        address: p.formattedAddress || '',
        latitude: p.location ? p.location.latitude : null,
        longitude: p.location ? p.location.longitude : null,
        types: Array.isArray(p.types) ? p.types.join(',') : '',
        type: Array.isArray(p.types) && p.types.length ? p.types[0] : '',
        icon_url: p.iconMaskBaseUri || null,
        is_competitor: false,
        is_ally: false,
        visible: true,
        color: 7
      }))

      // Sync buckets from saved markers in the target group
      if (targetGroup) {
        this.syncBucketsFromSavedMarkersForTargetGroup(targetGroup)
      }
    } catch (err) {
      console.error('Error in queryAndSetPlaceResultsForTargetGroup:', err)
      this.placeResults = []
      // Empty/error search must not drop items already classified into buckets
      this.replaceTemporaryMarkersPreservingClassified([])
    } finally {
      this.loading = false
    }
  }

  /** Sync buckets specifically for target group view (uses saved marker flags directly) */
  @action.bound
  syncBucketsFromSavedMarkersForTargetGroup (targetGroup) {
    if (!targetGroup) return
    const savedMarkersArr = targetGroup.savedMarkers ? targetGroup.savedMarkers.slice() : []
    const preserved = this.captureClassificationState()

    // Always clear and rebuild bucket state to ensure consistency
    // This prevents stale state from persisting across selections
    this.bucketById.clear()
    this.sharedBucketsById.clear()
    this.pendingBucketById.clear()
    this.disabledRowsById.clear()
    this.removedFromBucketById.clear()
    this.general = []
    this.allies = []
    this.competitors = []
    this.currentTargetGroupId = targetGroup.pk
    this.hasBucketChanges = false

    if (this.temporaryMarkers.length === 0) {
      this.restoreClassificationState(preserved)
      return
    }

    // For target group view, process all temporary markers
    for (const tempMarker of this.temporaryMarkers) {
      // Check if this marker has flags (either directly or via savedMarkerId lookup)
      let is_general = tempMarker.is_general
      let is_ally = tempMarker.is_ally
      let is_competitor = tempMarker.is_competitor

      // If marker has savedMarkerId, try to get flags from the saved marker
      const idStr = String(tempMarker.id)
      const savedMarkerId = tempMarker.savedMarkerId || (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : null)
      let savedMarker = null
      if (savedMarkerId) {
        savedMarker = _.find(savedMarkersArr, { id: savedMarkerId })
        if (savedMarker) {
          is_general = savedMarker.is_general
          is_ally = savedMarker.is_ally
          is_competitor = savedMarker.is_competitor
        }
      }

      // If this corresponds to an "old" saved marker (no flags at load time),
      // default it into the General bucket AND persist that change back to the backend.
      // SavedMarker constructor tagged such markers with _wasUnbucketedAtLoad.
      if (savedMarker && savedMarker._wasUnbucketedAtLoad) {
        is_general = true
        is_ally = false
        is_competitor = false

        // Update in-memory SavedMarker so map colors/tooltips are correct
        savedMarker.is_general = true
        savedMarker.is_ally = false
        savedMarker.is_competitor = false

        // Fire-and-forget migration patch to backend so this marker is permanently General.
        try {
          const validColorId = this.getValidColorId(savedMarker.color_id)
          const lat = savedMarker.position && savedMarker.position.latitude
          const lng = savedMarker.position && savedMarker.position.longitude
          const icon_url = savedMarker.icon_url || null
          const types = typeof savedMarker.types === 'string'
            ? savedMarker.types
            : (Array.isArray(savedMarker.types) ? savedMarker.types.join(',') : '')

          api.patchMarker(savedMarker.id, {
            name: savedMarker.name,
            description: savedMarker.description,
            latitude: lat,
            longitude: lng,
            target_group: targetGroup.pk,
            color: validColorId,
            is_general: true,
            is_competitor: false,
            is_ally: false,
            icon_url,
            types
          }).catch(err => {
            console.warn('Failed to migrate legacy saved marker to General bucket:', err)
          })
        } catch (e) {
          console.warn('Error while migrating legacy saved marker flags:', e)
        }
      }

      // Skip if no flags are set (and this is not a saved marker)
      if (!is_general && !is_ally && !is_competitor) continue

      // Build bucket list from flags
      const buckets = []
      if (is_general) buckets.push('general')
      if (is_ally) buckets.push('ally')
      if (is_competitor) buckets.push('competitor')

      if (buckets.length > 0) {
        // Normalize tempMarker flags so map rendering (TemporaryMarkerLayer)
        // uses the same bucket flags as SavedMarker / bucket state.
        tempMarker.is_general = is_general
        tempMarker.is_ally = is_ally
        tempMarker.is_competitor = is_competitor

        // Set baseline and current state
        this.sharedBucketsById.set(tempMarker.id, observable.array(buckets.slice()))
        this.bucketById.set(tempMarker.id, observable.array(buckets.slice()))
        this.disabledRowsById.set(tempMarker.id, true)

        // Add to bucket arrays (no duplicates since we cleared them above)
        for (const bucket of buckets) {
          if (bucket === 'general') this.general.push(tempMarker)
          else if (bucket === 'ally') this.allies.push(tempMarker)
          else if (bucket === 'competitor') this.competitors.push(tempMarker)
        }
      }
    }

    this.restoreClassificationState(preserved)
  }

  @action.bound
  clearPredictions () {
    this.placePredictions = []
    this.queryPredictions = []
  }

  isSamePoiMarker (a, b) {
    if (!a || !b) return false
    if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true
    const savedIdOf = (m) => {
      if (m.savedMarkerId != null) return m.savedMarkerId
      const idStr = String(m.id || '')
      if (idStr.startsWith('saved_')) {
        const parsed = parseInt(idStr.replace('saved_', ''), 10)
        return Number.isNaN(parsed) ? null : parsed
      }
      return null
    }
    const aSaved = savedIdOf(a)
    const bSaved = savedIdOf(b)
    if (aSaved != null && bSaved != null && aSaved === bSaved) return true
    const EPS = 1e-4
    const aLat = a.position && a.position.latLng && a.position.latLng.lat
    const aLng = a.position && a.position.latLng && a.position.latLng.lng
    const bLat = b.position && b.position.latLng && b.position.latLng.lat
    const bLng = b.position && b.position.latLng && b.position.latLng.lng
    if (aLat == null || bLat == null || aLng == null || bLng == null) return false
    return Math.abs(aLat - bLat) < EPS && Math.abs(aLng - bLng) < EPS
  }

  getClassifiedMarkers () {
    const ids = new Set()
    if (this.bucketById && typeof this.bucketById.forEach === 'function') {
      this.bucketById.forEach((buckets, markerId) => {
        if (buckets && buckets.length > 0) ids.add(markerId)
      })
    }
    const byId = new Map()
    const consider = (m) => {
      if (!m || m.id == null) return
      if (ids.has(m.id) && !byId.has(m.id)) byId.set(m.id, m)
    }
    ;(this.temporaryMarkers || []).forEach(consider)
    ;(this.general || []).forEach(consider)
    ;(this.allies || []).forEach(consider)
    ;(this.competitors || []).forEach(consider)
    return Array.from(byId.values())
  }

  captureClassificationState () {
    const copyMap = (obsMap) => {
      const entries = []
      if (!obsMap || typeof obsMap.forEach !== 'function') return entries
      obsMap.forEach((value, key) => {
        entries.push([key, Array.isArray(value) ? value.slice() : value])
      })
      return entries
    }
    return {
      markers: this.getClassifiedMarkers(),
      bucketById: copyMap(this.bucketById),
      removedFromBucketById: copyMap(this.removedFromBucketById),
      disabledRowsById: copyMap(this.disabledRowsById),
      pendingBucketById: copyMap(this.pendingBucketById),
      general: (this.general || []).slice(),
      allies: (this.allies || []).slice(),
      competitors: (this.competitors || []).slice(),
      hasBucketChanges: !!this.hasBucketChanges
    }
  }

  @action.bound
  restoreClassificationState (state) {
    if (!state) return

    const current = Array.isArray(this.temporaryMarkers) ? this.temporaryMarkers.slice() : []
    for (const marker of (state.markers || [])) {
      if (!marker) continue
      const exists = current.find(m => this.isSamePoiMarker(m, marker))
      if (!exists) current.push(marker)
    }
    this.temporaryMarkers = current

    const restoreMap = (obsMap, entries) => {
      if (!obsMap || !entries) return
      for (const [key, value] of entries) {
        obsMap.set(key, Array.isArray(value) ? observable.array(value.slice()) : value)
      }
    }
    restoreMap(this.bucketById, state.bucketById)
    restoreMap(this.removedFromBucketById, state.removedFromBucketById)
    restoreMap(this.disabledRowsById, state.disabledRowsById)
    restoreMap(this.pendingBucketById, state.pendingBucketById)

    const pushUnique = (arr, item) => {
      if (!item || item.id == null) return
      if (arr.findIndex(x => x && x.id === item.id) === -1) arr.push(item)
    }
    ;(state.general || []).forEach(m => pushUnique(this.general, m))
    ;(state.allies || []).forEach(m => pushUnique(this.allies, m))
    ;(state.competitors || []).forEach(m => pushUnique(this.competitors, m))

    if (state.hasBucketChanges) this.hasBucketChanges = true
  }

  @action.bound
  replaceTemporaryMarkersPreservingClassified (newMarkers) {
    const incoming = Array.isArray(newMarkers) ? newMarkers.slice() : []
    const classified = this.getClassifiedMarkers()
    const merged = incoming.slice()
    for (const classifiedMarker of classified) {
      const matchIdx = merged.findIndex(m => this.isSamePoiMarker(m, classifiedMarker))
      if (matchIdx === -1) {
        merged.push(classifiedMarker)
      } else if (merged[matchIdx].id !== classifiedMarker.id) {
        // Keep the classified marker object so bucket maps keyed by its id stay valid
        merged[matchIdx] = classifiedMarker
      }
    }
    this.temporaryMarkers = merged
    return merged
  }

  getMarkersForBucketDisplay () {
    const searchMarkers = Array.isArray(this.temporaryMarkers) ? this.temporaryMarkers : []
    const seenIds = new Set(searchMarkers.map(m => m && m.id).filter(id => id != null))
    const extras = []
    ;[...(this.general || []), ...(this.allies || []), ...(this.competitors || [])].forEach(m => {
      if (m && m.id != null && !seenIds.has(m.id)) {
        extras.push(m)
        seenIds.add(m.id)
      }
    })
    return extras.length ? searchMarkers.concat(extras) : searchMarkers
  }

  @action.bound
  clearSearchResults () {
    // Clear search results
    this.placeResults = []
    // Clear temporaryMarkers but preserve:
    // 1. Markers that are in buckets
    // 2. Saved markers
    const classifiedIds = new Set(this.getClassifiedMarkers().map(m => m.id))
    // Keep markers that are in buckets OR are saved markers
    this.temporaryMarkers = this.temporaryMarkers.filter(marker => {
      if (!marker || !marker.id) return false
      // Keep if in buckets
      if (classifiedIds.has(marker.id)) return true
      // Keep if saved marker
      const idStr = String(marker.id)
      if (marker.savedMarkerId || idStr.startsWith('saved_')) return true
      return false
    })
  }

  // ---------- Processing and filtering ----------
  @action.bound
  serializePlaces (places) {
    if (!places || !Array.isArray(places)) return []

    let polygonCoordinates = null
    try {
      const sel = (selectionStore && (selectionStore.item || selectionStore._item)) || null
      if (sel && sel.geoJSON) {
        const gj = sel.geoJSON
        if (gj.type === 'Polygon') {
          polygonCoordinates = gj.coordinates && gj.coordinates[0]
        } else if (gj.type === 'MultiPolygon') {
          const ok =
            Array.isArray(gj.coordinates) &&
            Array.isArray(gj.coordinates[0]) &&
            Array.isArray(gj.coordinates[0][0])
          polygonCoordinates = ok ? gj.coordinates[0][0] : null
        }
      }
    } catch (e) {
      console.warn('serializePlaces polygon error:', e)
    }

    const filtered = places.filter((p) => {
      if (!polygonCoordinates) return true
      const loc = p && p.location
      if (!loc || typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') return false
      const point = [loc.longitude, loc.latitude]
      return this.isPointInPolygon(point, polygonCoordinates)
    })

    // stable id
    const newMarkers = filtered
      .filter(p => p.location && typeof p.location.latitude === 'number' && typeof p.location.longitude === 'number')
      .map(p => {
        const stableId = p.id || p.placeId || (p.location ? (`${p.location.latitude},${p.location.longitude}`) : Math.random().toString(36).slice(2))
        return {
          id: stableId,
          position: { latLng: { lat: p.location.latitude, lng: p.location.longitude } },
          name: (p.displayName && p.displayName.text) || '',
          address: p.formattedAddress || '',
          type: Array.isArray(p.types) && p.types.length ? p.types[0] : '',
          types: p.types || [],
          getColor: 7,
          icon_url: p.iconMaskBaseUri || null
        }
      })
    // Merge classified POIs into the new search results instead of replacing
    // the whole list. A new search must not clear General / Comp / Ally items.
    this.replaceTemporaryMarkersPreservingClassified(newMarkers)

    return filtered.map(p => ({
      id: p.id,
      place_id: p.id,
      name: (p.displayName && p.displayName.text) || '',
      description: '',
      address: p.formattedAddress || '',
      latitude: p.location ? p.location.latitude : null,
      longitude: p.location ? p.location.longitude : null,
      types: Array.isArray(p.types) ? p.types.join(',') : '',
      type: Array.isArray(p.types) && p.types.length ? p.types[0] : '',
      icon_url: p.iconMaskBaseUri || null,
      is_competitor: false,
      is_ally: false,
      visible: true,
      color: 7
    }))
  }

  // Robust point-in-polygon (split variable lines because of ESLint)
  isPointInPolygon (pointLngLat, polygonRing) {
    if (!Array.isArray(polygonRing) || polygonRing.length < 3) return false
    let inside = false
    const x = pointLngLat[0]
    const y = pointLngLat[1]
    for (let i = 0, j = polygonRing.length - 1; i < polygonRing.length; j = i++) {
      const xi = polygonRing[i][0]
      const yi = polygonRing[i][1]
      const xj = polygonRing[j][0]
      const yj = polygonRing[j][1]
      const numerator = (xj - xi) * (y - yi)
      const denominator = (yj - yi)
      const xIntersect = (denominator !== 0) ? (numerator / denominator) + xi : xi
      const condY = (yi > y) !== (yj > y)
      const condX = x < xIntersect
      const intersect = condY && condX
      if (intersect) inside = !inside
    }
    return inside
  }

  // util
  convertLeafletBoundsToNearbyCircle (bounds) {
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const center = {
      latitude: (sw.lat + ne.lat) / 2,
      longitude: (sw.lng + ne.lng) / 2
    }
    const radius = Math.max(
      50,
      Math.ceil(this._haversineMeters(
        { lat: center.latitude, lng: center.longitude },
        { lat: ne.lat, lng: ne.lng }
      ))
    )
    return { circle: { center, radius } }
  }

  _toRad (deg) { return deg * Math.PI / 180 }
  _haversineMeters (a, b) {
    const R = 6371000
    const dLat = this._toRad(b.lat - a.lat)
    const dLng = this._toRad(b.lng - a.lng)
    const la1 = this._toRad(a.lat)
    const la2 = this._toRad(b.lat)
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) ** 1 * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(h))
  }

  /** Move markers based on current selection to another target group */
  @action.bound
  async moveMarkersToTargetGroup (dataStore, targetGroupId) {
    if (!targetGroupId) {
      throw new Error('Target group ID is required to move markers')
    }

    const selectedItem = selectionStore.item
    const selectedType = selectionStore.type
    // When a target group is selected, selectedItem IS the target group
    // When a shape or marker is selected, sourceGroup is in selectionStore.data.group
    let sourceGroup = null
    if (selectedType === selectionTypes.TARGET_GROUP) {
      sourceGroup = selectedItem
    } else {
      sourceGroup = selectionStore.data && selectionStore.data.group
    }

    if (!sourceGroup) {
      throw new Error('No source group found for moving markers')
    }

    if (sourceGroup.pk === targetGroupId) {
      return targetGroupId // Same group, no move needed
    }

    // Ensure we have the latest saved markers
    await sourceGroup.loadSavedMarkers()

    // Get destination group
    let targetGroup = _.find(dataStore.targetGroups, { pk: targetGroupId })
    if (!targetGroup) {
      await dataStore.loadTargetGroups()
      targetGroup = _.find(dataStore.targetGroups, { pk: targetGroupId })
    }
    if (!targetGroup) {
      throw new Error('Destination target group not found')
    }
    await targetGroup.loadSavedMarkers()

    const EPS = 1e-4
    // Helper to check if a marker already exists in destination group
    const markerExistsInDestination = (lat, lng) => {
      if (!targetGroup.savedMarkers || lat == null || lng == null) return false
      return targetGroup.savedMarkers.some(sm => {
        const smLat = sm.position && sm.position.latitude
        const smLng = sm.position && sm.position.longitude
        return smLat != null && smLng != null &&
          Math.abs(smLat - lat) < EPS && Math.abs(smLng - lng) < EPS
      })
    }

    // Collect markers to move: both saved markers and unsaved POI results
    let savedMarkersToMove = []
    let tempMarkersToMove = []

    if (selectedType === selectionTypes.TARGET_GROUP) {
      // Move all markers in the target group (saved + unsaved)
      // Exclude markers that already exist in destination
      savedMarkersToMove = sourceGroup.savedMarkers
        ? sourceGroup.savedMarkers.filter(marker => {
          if (!marker.position || marker.position.latitude == null || marker.position.longitude == null) {
            return false
          }
          return !markerExistsInDestination(marker.position.latitude, marker.position.longitude)
        })
        : []
      // Include all temporary markers (unsaved POI results) that are in the current view
      // Exclude markers that already exist in destination
      tempMarkersToMove = this.temporaryMarkers
        ? this.temporaryMarkers.filter(tempMarker => {
          if (!tempMarker.position || !tempMarker.position.latLng) return false
          const tempLat = tempMarker.position.latLng.lat
          const tempLng = tempMarker.position.latLng.lng
          return !markerExistsInDestination(tempLat, tempLng)
        })
        : []
    } else if (selectedType === selectionTypes.GEOPOLYGON || selectedType === selectionTypes.BOUNDARY) {
      // Move only markers within the selected shape
      const shapeGeoJSON = selectedItem && selectedItem.geoJSON
      if (shapeGeoJSON) {
        if (sourceGroup.savedMarkers) {
          savedMarkersToMove = sourceGroup.savedMarkers.filter(marker => {
            if (!marker.position || marker.position.latitude == null || marker.position.longitude == null) {
              return false
            }
            // Check if inside shape AND not already in destination
            return this.pointInGeoJSON(
              marker.position.latitude,
              marker.position.longitude,
              shapeGeoJSON
            ) && !markerExistsInDestination(marker.position.latitude, marker.position.longitude)
          })
        }
        // Include temporary markers that are within the shape
        // Exclude markers that already exist in destination
        if (this.temporaryMarkers) {
          tempMarkersToMove = this.temporaryMarkers.filter(tempMarker => {
            if (!tempMarker.position || !tempMarker.position.latLng) return false
            const tempLat = tempMarker.position.latLng.lat
            const tempLng = tempMarker.position.latLng.lng
            return this.pointInGeoJSON(tempLat, tempLng, shapeGeoJSON) &&
              !markerExistsInDestination(tempLat, tempLng)
          })
        }
      }
    } else if (selectedType === selectionTypes.SAVED_MARKER) {
      // Move only the selected marker (if not already in destination)
      if (selectedItem) {
        const markerLat = selectedItem.position && selectedItem.position.latitude
        const markerLng = selectedItem.position && selectedItem.position.longitude
        if (markerLat != null && markerLng != null && !markerExistsInDestination(markerLat, markerLng)) {
          savedMarkersToMove = [selectedItem]
        }
      }
    } else {
      throw new Error('Unsupported selection type for moving markers')
    }

    // First, save any unsaved POI results (tempMarkers) to the destination group
    // ONLY if they are in buckets (have at least one bucket flag)
    if (tempMarkersToMove.length > 0) {
      // Build a map of temp marker id -> bucket flags
      const tempMarkerData = new Map()
      for (const tempMarker of tempMarkersToMove) {
        const buckets = this.bucketById.get(tempMarker.id) || []
        // Skip markers that are not in any bucket
        if (!buckets || buckets.length === 0) continue
        tempMarkerData.set(tempMarker.id, {
          marker: tempMarker,
          is_general: buckets.includes('general'),
          is_competitor: buckets.includes('competitor'),
          is_ally: buckets.includes('ally')
        })
      }

      // Save each temp marker to destination group
      const EPS = 1e-4

      for (const data of tempMarkerData.values()) {
        const tempMarker = data.marker
        if (!tempMarker) continue

        const tempLat = tempMarker.position && tempMarker.position.latLng && tempMarker.position.latLng.lat
        const tempLng = tempMarker.position && tempMarker.position.latLng && tempMarker.position.latLng.lng
        if (tempLat == null || tempLng == null) continue

        try {
          // Check if marker already exists in destination (by lat/lng)
          const existingMarker = targetGroup.savedMarkers && targetGroup.savedMarkers.find(sm => {
            const smLat = sm.position && sm.position.latitude
            const smLng = sm.position && sm.position.longitude
            return smLat != null && smLng != null &&
              Math.abs(smLat - tempLat) < EPS && Math.abs(smLng - tempLng) < EPS
          })

          if (!existingMarker) {
            // Create new marker in destination group with bucket flags
            const validColorId = this.getValidColorId()

            // eslint-disable-next-line no-await-in-loop
            await api.postMarker({
              name: tempMarker.name || 'POI',
              description: tempMarker.address || '',
              latitude: tempLat,
              longitude: tempLng,
              target_group: targetGroupId,
              color: validColorId,
              is_general: data.is_general,
              is_competitor: data.is_competitor,
              is_ally: data.is_ally,
              icon_url: tempMarker.icon_url || null,
              place_id: tempMarker.place_id || null,
              types: Array.isArray(tempMarker.types)
                ? tempMarker.types.join(',')
                : (tempMarker.types || '')
            })
          } else {
            // Update existing marker with new bucket flags
            const validColorId = this.getValidColorId(existingMarker.color)

            // eslint-disable-next-line no-await-in-loop
            await api.patchMarker(existingMarker.id, {
              name: existingMarker.name,
              description: existingMarker.description,
              latitude: tempLat,
              longitude: tempLng,
              target_group: targetGroupId,
              color: validColorId,
              is_general: data.is_general,
              is_competitor: data.is_competitor,
              is_ally: data.is_ally,
              icon_url: tempMarker.icon_url || existingMarker.icon_url || null,
              types: Array.isArray(tempMarker.types)
                ? tempMarker.types.join(',')
                : (tempMarker.types || existingMarker.types || '')
            })
          }
        } catch (err) {
          throw new Error(`Failed to save marker "${tempMarker.name}": ${err.message || err}`)
        }
      }

      // Reload destination markers to get the newly saved ones
      await targetGroup.loadSavedMarkers()
    }

    // Now move saved markers from source to destination
    // Preserve any bucket changes that were made in the POI menu
    const markersToMove = savedMarkersToMove

    // Reload destination markers once before the loop to ensure we have the latest (including any just saved from tempMarkers)
    await targetGroup.loadSavedMarkers()
    // Reload source markers once before the loop to get latest state
    await sourceGroup.loadSavedMarkers()

    // Move each marker to the destination group, preserving bucket changes
    // Sequential processing is intentional to ensure data integrity

    for (const marker of markersToMove) {
      try {
        const markerLat = marker.position && marker.position.latitude
        const markerLng = marker.position && marker.position.longitude
        if (markerLat == null || markerLng == null) continue

        // Check if marker already exists in destination group (by lat/lng)
        const existingInDestination = targetGroup.savedMarkers && targetGroup.savedMarkers.find(sm => {
          const smLat = sm.position && sm.position.latitude
          const smLng = sm.position && sm.position.longitude
          return smLat != null && smLng != null &&
            Math.abs(smLat - markerLat) < EPS && Math.abs(smLng - markerLng) < EPS
        })

        // Find matching temp marker to get current bucket assignments
        let bucketFlags = {
          is_general: marker.is_general,
          is_competitor: marker.is_competitor,
          is_ally: marker.is_ally
        }

        if (this.temporaryMarkers) {
          const matchingTempMarker = this.temporaryMarkers.find(tm => {
            const tmLat = tm.position && tm.position.latLng && tm.position.latLng.lat
            const tmLng = tm.position && tm.position.latLng && tm.position.latLng.lng
            return tmLat != null && tmLng != null &&
              Math.abs(tmLat - markerLat) < EPS && Math.abs(tmLng - markerLng) < EPS
          })

          if (matchingTempMarker) {
            // Use current bucket assignments from bucketById if available
            const buckets = this.bucketById.get(matchingTempMarker.id) || []
            bucketFlags = {
              is_general: buckets.includes('general'),
              is_competitor: buckets.includes('competitor'),
              is_ally: buckets.includes('ally')
            }
          }
        }

        if (existingInDestination) {
          // Marker already exists in destination - just update it with current bucket flags
          // Don't create a duplicate, just update the existing one
          const validColorId = this.getValidColorId(existingInDestination.color)

          // eslint-disable-next-line no-await-in-loop
          await api.patchMarker(existingInDestination.id, {
            name: existingInDestination.name,
            description: existingInDestination.description,
            latitude: markerLat,
            longitude: markerLng,
            target_group: targetGroupId,
            color: validColorId,
            is_general: bucketFlags.is_general,
            is_competitor: bucketFlags.is_competitor,
            is_ally: bucketFlags.is_ally,
            icon_url: existingInDestination.icon_url || null,
            types: existingInDestination.types || ''
          })
          // Check if marker is still in source group before deleting
          // eslint-disable-next-line no-await-in-loop
          await sourceGroup.loadSavedMarkers()
          const stillInSource = sourceGroup.savedMarkers && sourceGroup.savedMarkers.some(sm => {
            return sm.id === marker.id
          })
          if (stillInSource && sourceGroup.pk !== targetGroupId) {
            // Marker exists in both source and destination - delete from source to complete the move
            // eslint-disable-next-line no-await-in-loop
            await sourceGroup.deleteSavedMarker(marker)
          }
        } else {
          // Marker doesn't exist in destination - copy it
          // eslint-disable-next-line no-await-in-loop
          await marker.copy(targetGroupId)
          // Update the copied marker with current bucket flags
          // Reload destination to find the newly copied marker
          // eslint-disable-next-line no-await-in-loop
          await targetGroup.loadSavedMarkers()
          const copiedMarker = targetGroup.savedMarkers && targetGroup.savedMarkers.find(sm => {
            const smLat = sm.position && sm.position.latitude
            const smLng = sm.position && sm.position.longitude
            return smLat != null && smLng != null &&
              Math.abs(smLat - markerLat) < EPS && Math.abs(smLng - markerLng) < EPS &&
              sm.name === marker.name
          })

          if (copiedMarker) {
            // Update with current bucket flags
            const validColorId = this.getValidColorId(copiedMarker.color)

            // eslint-disable-next-line no-await-in-loop
            await api.patchMarker(copiedMarker.id, {
              name: copiedMarker.name,
              description: copiedMarker.description,
              latitude: markerLat,
              longitude: markerLng,
              target_group: targetGroupId,
              color: validColorId,
              is_general: bucketFlags.is_general,
              is_competitor: bucketFlags.is_competitor,
              is_ally: bucketFlags.is_ally,
              icon_url: copiedMarker.icon_url || null,
              types: copiedMarker.types || ''
            })
          }

          // Delete from source group only if source and destination are different
          if (sourceGroup.pk !== targetGroupId) {
            // eslint-disable-next-line no-await-in-loop
            await sourceGroup.deleteSavedMarker(marker)
          }
        }
        // Remove from POI bucket state
        this.removeMarkerFromAllBucketsBySavedMarker(marker)
      } catch (err) {
        console.warn('Failed to move marker:', marker.name, err)
      }
    }

    // Reload data for both groups
    await sourceGroup.loadSavedMarkers()
    // targetGroup was already loaded earlier, just reload its markers
    if (targetGroup) {
      await targetGroup.loadSavedMarkers()
      targetGroup.expanded = true
    }

    this.resetBucketChanges()
    return targetGroupId
  }

  /** Move the currently selected saved item into another target group */
  @action.bound
  async moveSelectionToTargetGroup (dataStore, targetGroupId) {
    if (!targetGroupId) {
      throw new Error('Target group ID is required to move the selection')
    }

    const selectedItem = selectionStore.item
    const selectedType = selectionStore.type
    const sourceGroup = selectionStore.data && selectionStore.data.group

    if (!selectedItem || !selectedType || !sourceGroup) {
      throw new Error('No selected item with source group to move')
    }

    if (sourceGroup.pk === targetGroupId) {
      return targetGroupId
    }

    if (selectedType === selectionTypes.GEOPOLYGON && typeof selectedItem.copy === 'function') {
      await selectedItem.copy(targetGroupId)
      // When moving a shape, keep its markers in the original target group
      await sourceGroup.deleteGeopolygon(selectedItem, { keepMarkers: true })
    } else if (selectedType === selectionTypes.SAVED_MARKER && typeof selectedItem.copy === 'function') {
      await selectedItem.copy(targetGroupId)
      await sourceGroup.deleteSavedMarker(selectedItem)
      // Also remove from POI bucket state
      this.removeMarkerFromAllBucketsBySavedMarker(selectedItem)
    } else if (selectedType === selectionTypes.BOUNDARY && typeof selectedItem.copy === 'function') {
      await selectedItem.copy(targetGroupId)
      // When moving a boundary, keep its markers in the original target group
      await sourceGroup.deleteBoundary(selectedItem, { keepMarkers: true })
    } else {
      throw new Error('Selected item type is not supported for Save to Folder moves')
    }

    selectionStore.clear()

    let targetGroup = _.find(dataStore.targetGroups, { pk: targetGroupId })
    if (!targetGroup) {
      await dataStore.loadTargetGroups()
      targetGroup = _.find(dataStore.targetGroups, { pk: targetGroupId })
    }

    if (targetGroup) {
      await targetGroup.loadGeometries()
      await targetGroup.loadBoundaries()
      await targetGroup.loadSavedMarkers()
    }

    this.resetBucketChanges()
    return targetGroupId
  }

  @action.bound
  markBucketChange () {
    // Any change to bucket membership in non-draft flow should mark that
    // there are unsaved changes, so that Save / Save To / Reset buttons
    // become visible in the POI menu.
    this.hasBucketChanges = true
  }

  @action.bound
  resetBucketChanges () {
    this.hasBucketChanges = false
  }

  /** Sync buckets from saved markers in the target group (for loading persisted state) */
  @action.bound
  syncBucketsFromSavedMarkers (targetGroup) {
    if (!targetGroup || !targetGroup.savedMarkers) return

    // Snapshot unsaved bucket items first. This rebuilds baseline from the DB
    // without discarding POIs the user already classified in this session.
    const preserved = this.captureClassificationState()

    // Always clear and rebuild bucket state to ensure consistency
    this.bucketById.clear()
    this.sharedBucketsById.clear()
    this.pendingBucketById.clear()
    this.disabledRowsById.clear()
    this.removedFromBucketById.clear()
    this.general = []
    this.allies = []
    this.competitors = []
    this.currentTargetGroupId = targetGroup.pk
    this.hasBucketChanges = false

    // Match saved markers to temporary markers: prefer id (avoids Google row vs saved row at same coords)
    const EPS = 1e-4
    for (const savedMarker of targetGroup.savedMarkers) {
      const lat = savedMarker.position && savedMarker.position.latitude
      const lng = savedMarker.position && savedMarker.position.longitude
      if (lat == null || lng == null) continue

      const sid = savedMarker.id
      let tempMarker = this.temporaryMarkers.find(m => {
        if (!m) return false
        if (m.savedMarkerId === sid) return true
        const mid = String(m.id || '')
        return mid === `saved_${sid}`
      })
      if (!tempMarker) {
        tempMarker = this.temporaryMarkers.find(m => {
          const mLat = m.position && m.position.latLng && m.position.latLng.lat
          const mLng = m.position && m.position.latLng && m.position.latLng.lng
          return mLat != null && mLng != null &&
            Math.abs(mLat - lat) < EPS && Math.abs(mLng - lng) < EPS
        })
      }

      if (!tempMarker) continue

      // Build bucket list from saved flags
      const buckets = []
      if (savedMarker.is_general) buckets.push('general')
      if (savedMarker.is_ally) buckets.push('ally')
      if (savedMarker.is_competitor) buckets.push('competitor')

      // If this saved marker has no bucket flags yet, default it to General.
      // This makes all already-saved POIs appear in the General category by default.
      if (buckets.length === 0) {
        buckets.push('general')
      }

      // Set baseline and current state
      this.sharedBucketsById.set(tempMarker.id, observable.array(buckets.slice()))
      this.bucketById.set(tempMarker.id, observable.array(buckets.slice()))
      this.disabledRowsById.set(tempMarker.id, true)

      // Add to bucket arrays
      for (const bucket of buckets) {
        if (bucket === 'general') this.general.push(tempMarker)
        else if (bucket === 'ally') this.allies.push(tempMarker)
        else if (bucket === 'competitor') this.competitors.push(tempMarker)
      }
    }

    // Overlay session classifications so a new search does not clear unsaved buckets
    this.restoreClassificationState(preserved)
  }

  /** Get a valid color ID from available marker colors */
  getValidColorId (existingColorId = null) {
    if (existingColorId && dataStore && dataStore.markerColors) {
      const colorExists = dataStore.markerColors.some(c => c.id === existingColorId)
      if (colorExists) {
        return existingColorId
      }
    }
    const grayColor = getDefaultGrayColor(dataStore && dataStore.markerColors)
    if (grayColor) return grayColor.id
    if (dataStore && dataStore.markerColors && dataStore.markerColors.length > 0) {
      return dataStore.markerColors[0].id
    }
    return 1
  }

  /** Save bucket changes for existing item (non-draft mode) - creates/updates markers via API */
  @action.bound
  async saveBucketsToExistingGroup (targetGroup, options = {}) {
    if (!targetGroup) {
      return
    }

    const bulkCreateMarkers = []

    // Build a map of temporary marker id -> bucket flags and marker data.
    // IMPORTANT: Only save markers that are in buckets (have at least one bucket flag).
    const markerById = new Map()
    // Only collect markers that have explicit bucket membership.
    this.bucketById.forEach((buckets, id) => {
      // Skip if marker has no buckets (empty array)
      if (!buckets || buckets.length === 0) return
      const marker = this.temporaryMarkers.find(m => m.id === id)
      if (!marker) return

      markerById.set(id, {
        marker,
        is_general: !!(buckets && buckets.includes('general')),
        is_competitor: !!(buckets && buckets.includes('competitor')),
        is_ally: !!(buckets && buckets.includes('ally'))
      })
    })

    // For each temporary marker we know about (bucketed or not)
    for (const data of markerById.values()) {
      const tempMarker = data.marker
      if (!tempMarker) continue

      const tempLat = tempMarker.position && tempMarker.position.latLng && tempMarker.position.latLng.lat
      const tempLng = tempMarker.position && tempMarker.position.latLng && tempMarker.position.latLng.lng
      if (tempLat == null || tempLng == null) continue

      // Check if a saved marker already exists for this temp marker
      // Prefer matching by savedMarkerId (for markers added via tools), then fall back to lat/lng
      let existingSavedMarker = null
      const idStr = String(tempMarker.id)
      const savedId = tempMarker.savedMarkerId || (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : null)

      if (savedId && targetGroup.savedMarkers) {
        existingSavedMarker = targetGroup.savedMarkers.find(sm => sm.id === savedId)
      }

      // Never match a new/search row to an existing DB marker by lat/lng alone — coordinates
      // can match a different shape's POI or a manual pin, and PATCH would overwrite/remove
      // the wrong marker from the user's perspective. Only merge when Google place id matches.
      if (!existingSavedMarker && targetGroup.savedMarkers && !idStr.startsWith('saved_') && !tempMarker.savedMarkerId) {
        const placeKeys = new Set()
        if (tempMarker.place_id) placeKeys.add(String(tempMarker.place_id))
        if (tempMarker.placeId) placeKeys.add(String(tempMarker.placeId))
        if (tempMarker.id) placeKeys.add(String(tempMarker.id))
        existingSavedMarker = targetGroup.savedMarkers.find(sm => {
          const smPid = sm.place_id != null ? String(sm.place_id) : (sm.placeId != null ? String(sm.placeId) : '')
          return smPid && placeKeys.has(smPid)
        })
      }

      // Immediately update local flags so map marker colors react without reload
      // (for both temp markers used in POI/TemporaryMarkerLayer and SavedMarker
      // instances used in SavedMarkerLayer).
      tempMarker.is_general = data.is_general
      tempMarker.is_ally = data.is_ally
      tempMarker.is_competitor = data.is_competitor

      try {
        if (existingSavedMarker) {
          existingSavedMarker.is_general = data.is_general
          existingSavedMarker.is_ally = data.is_ally
          existingSavedMarker.is_competitor = data.is_competitor

          // Update existing saved marker on the backend with new flags.
          // NOTE: backend CreateMarkerSerializer used for PATCH expects full payload,
          // so we must also send latitude, longitude, target_group and color.
          const icon_url = tempMarker.icon_url || existingSavedMarker.icon_url || null
          const types = Array.isArray(tempMarker.types)
            ? tempMarker.types.join(',')
            : (tempMarker.types || existingSavedMarker.types || '')

          const validColorId = this.getValidColorId(existingSavedMarker.color)

          // eslint-disable-next-line no-await-in-loop
          await api.patchMarker(existingSavedMarker.id, {
            name: existingSavedMarker.name,
            description: existingSavedMarker.description,
            latitude: tempLat,
            longitude: tempLng,
            target_group: targetGroup.pk,
            color: validColorId,
            is_general: data.is_general,
            is_competitor: data.is_competitor,
            is_ally: data.is_ally,
            icon_url,
            types
          })
        } else {
          // Create new marker with bucket flags (bulk request)
          const validColorId = this.getValidColorId()

          // Backend expects type as a single string, not array or comma-separated
          // Handle both tempMarker.type and tempMarker.types (either could be array or string)
          let typeValue = null
          if (Array.isArray(tempMarker.type) && tempMarker.type.length > 0) {
            typeValue = tempMarker.type[0] // Take first type from array
          } else if (typeof tempMarker.type === 'string' && tempMarker.type.trim().length > 0) {
            typeValue = tempMarker.type
          } else if (Array.isArray(tempMarker.types) && tempMarker.types.length > 0) {
            typeValue = tempMarker.types[0] // Take first type from types array
          } else if (typeof tempMarker.types === 'string' && tempMarker.types.trim().length > 0) {
            typeValue = tempMarker.types
          }

          const safeType = typeValue && String(typeValue).trim().length > 0
            ? String(typeValue).trim()
            : 'point_of_interest'

          bulkCreateMarkers.push({
            name: tempMarker.name || 'POI',
            place_id: tempMarker.id,
            description: tempMarker.address || tempMarker.description || '',
            latitude: tempLat,
            longitude: tempLng,
            color: validColorId,
            is_general: data.is_general,
            is_competitor: data.is_competitor,
            is_ally: data.is_ally,
            icon_url: tempMarker.icon_url || null,
            type: safeType
          })
        }
      } catch (err) {
        throw new Error(`Failed to save marker "${tempMarker.name}": ${err.message || err}`)
      }
    }

    if (bulkCreateMarkers.length > 0) {
      try {
        await api.postMarkersBulk({
          target_group: targetGroup.pk,
          markers: bulkCreateMarkers
        })
      } catch (err) {
        throw new Error(`Failed to save markers: ${err.message || err}`)
      }
    }

    // Handle markers that were removed from all buckets
    // Delete them from the target group since they're no longer in any bucket
    // If options.mergeOnly is true, SKIP this deletion step to prevent overwriting existing markers
    if (!options.mergeOnly && targetGroup.savedMarkers) {
      for (const savedMarker of targetGroup.savedMarkers) {
        const smId = savedMarker.id
        // Only the explicit saved_* / savedMarkerId row can justify a delete — never lat/lng,
        // or a Google row at the same coordinates can pick up empty buckets and delete the wrong DB marker.
        const tempMarker = this.temporaryMarkers.find(m => {
          if (!m) return false
          if (m.savedMarkerId === smId) return true
          const mid = String(m.id || '')
          return mid === `saved_${smId}`
        })
        // No saved_* row for this id in the current POI list (e.g. other shape / not in view).
        if (!tempMarker) {
          continue
        }
        // Skip if this marker is currently in a bucket (it was handled in the first loop
        // via markerById constructed from bucketById). This prevents touching markers
        // that were moved to a different bucket.
        const buckets = this.bucketById.get(tempMarker.id)
        if (buckets && buckets.length > 0) {
          continue
        }

        // Safety guard: delete only when this marker was explicitly removed
        // from one or more buckets in this POI session. This avoids deleting
        // older/existing markers due to id-mapping or partial UI state.
        const markerRemovedBuckets = this.removedFromBucketById.get(tempMarker.id)
        const wasExplicitlyRemoved = Array.isArray(markerRemovedBuckets) && markerRemovedBuckets.length > 0
        if (!wasExplicitlyRemoved) {
          continue
        }

        const wasInGeneral = savedMarker.is_general
        const wasInAlly = savedMarker.is_ally
        const wasInCompetitor = savedMarker.is_competitor

        // Marker is not in any bucket now - if it had flags before, delete it
        // since markers should only exist if they're in at least one bucket
        if (wasInGeneral || wasInAlly || wasInCompetitor) {
          try {
            // Delete the marker from the backend
            // eslint-disable-next-line no-await-in-loop
            await api.deleteMarker(savedMarker.id)
            // Update local state - remove from savedMarkers array
            if (targetGroup.savedMarkers) {
              const index = targetGroup.savedMarkers.findIndex(sm => sm.id === savedMarker.id)
              if (index !== -1) {
                targetGroup.savedMarkers.splice(index, 1)
              }
            }
            // Update temp marker flags for instant UI feedback
            if (tempMarker) {
              tempMarker.is_general = false
              tempMarker.is_ally = false
              tempMarker.is_competitor = false
            }
          } catch (err) {
            throw new Error(`Failed to delete marker "${savedMarker.name}": ${err.message || err}`)
          }
        }
      }
    }

    // Reload saved markers to get updated state (for future selections)
    await targetGroup.loadSavedMarkers()
    // Force re-render to update map marker colors
    this.temporaryMarkers = this.temporaryMarkers.slice()

    // Sync temporaryMarkers with freshly loaded savedMarkers to ensure flags are up-to-date
    // This ensures map markers and tools menu reflect the latest state
    if (targetGroup.savedMarkers && this.temporaryMarkers) {
      const EPS = 1e-4
      for (const tempMarker of this.temporaryMarkers) {
        if (!tempMarker) continue
        const tempLat = tempMarker.position && tempMarker.position.latLng && tempMarker.position.latLng.lat
        const tempLng = tempMarker.position && tempMarker.position.latLng && tempMarker.position.latLng.lng
        if (tempLat == null || tempLng == null) continue

        // Find matching saved marker
        const savedMarker = targetGroup.savedMarkers.find(sm => {
          const smLat = sm.position && sm.position.latitude
          const smLng = sm.position && sm.position.longitude
          return smLat != null && smLng != null &&
            Math.abs(smLat - tempLat) < EPS && Math.abs(smLng - tempLng) < EPS
        })

        if (savedMarker) {
          // Update temp marker flags to match saved marker
          tempMarker.is_general = savedMarker.is_general
          tempMarker.is_ally = savedMarker.is_ally
          tempMarker.is_competitor = savedMarker.is_competitor
        }
      }
    }

    // After saving, KEEP the current bucket state in the UI
    // and treat it as the new baseline for Reset.
    // This avoids wiping the bucket cards right after save and
    // relies on bucketById (which we just used to persist flags)
    // as the source of truth.
    this.sharedBucketsById.clear()
    this.removedFromBucketById.clear()
    this.bucketById.forEach((buckets, id) => {
      if (buckets && buckets.length > 0) {
        this.sharedBucketsById.set(id, observable.array(buckets.slice()))
      }
    })

    // Force re-render of TemporaryMarkerLayer by replacing the array reference
    // so that map-marker__icon colors update immediately based on new flags.
    this.temporaryMarkers = this.temporaryMarkers.slice()

    // Reset the changes flag since we just saved
    this.hasBucketChanges = false
  }

  /** Reset buckets to baseline (persisted) state - for Reset button */
  @action.bound
  resetToBaseline () {
    // Clear current state
    this.bucketById.clear()
    this.pendingBucketById.clear()
    this.disabledRowsById.clear()
    this.removedFromBucketById.clear()
    this.general = []
    this.allies = []
    this.competitors = []

    // Restore from baseline
    this.sharedBucketsById.forEach((buckets, id) => {
      const tempMarker = this.temporaryMarkers.find(m => m.id === id)
      if (!tempMarker) return

      const bucketsCopy = buckets.slice()
      this.bucketById.set(id, observable.array(bucketsCopy))
      this.disabledRowsById.set(id, true)

      for (const bucket of bucketsCopy) {
        if (bucket === 'general' && !this.general.find(x => x.id === id)) {
          this.general.push(tempMarker)
        } else if (bucket === 'ally' && !this.allies.find(x => x.id === id)) {
          this.allies.push(tempMarker)
        } else if (bucket === 'competitor' && !this.competitors.find(x => x.id === id)) {
          this.competitors.push(tempMarker)
        }
      }
    })

    this.hasBucketChanges = false
  }

  @action.bound
  cancelPoiChanges () {
    // Identify temporary markers that are NOT linked to a saved marker
    // (no savedMarkerId and id not in the "saved_X" format).
    const unsavedIds = this.temporaryMarkers
      .filter(m => {
        const idStr = String(m.id || '')
        const isSavedId = idStr.startsWith('saved_')
        return !m.savedMarkerId && !isSavedId
      })
      .map(m => m.id)

    if (unsavedIds.length > 0) {
      const idSet = new Set(unsavedIds)

      // Remove unsaved markers from temporaryMarkers
      this.temporaryMarkers = this.temporaryMarkers.filter(m => !idSet.has(m.id))

      // Also remove corresponding entries from placeResults, matching
      // both by id and by lat/lng when possible.
      this.placeResults = this.placeResults.filter(p => {
        const pId = p.id || p.place_id
        if (idSet.has(pId)) return false
        if (p.location && typeof p.location.latitude === 'number' && typeof p.location.longitude === 'number') {
          const lat = p.location.latitude
          const lng = p.location.longitude
          const matchedUnsaved = this.temporaryMarkers.find(m => {
            if (!m.position || !m.position.latLng) return false
            const mLat = m.position.latLng.lat
            const mLng = m.position.latLng.lng
            const EPS = 1e-6
            return Math.abs(mLat - lat) < EPS && Math.abs(mLng - lng) < EPS
          })
          if (matchedUnsaved) return false
        }
        return true
      })

      // Clean bucket maps and arrays for unsaved markers
      const cleanForId = (id) => {
        this.bucketById.delete(id)
        this.pendingBucketById.delete(id)
        this.disabledRowsById.delete(id)
        this.removedFromBucketById.delete(id)
        const removeFrom = (arr) => {
          const idx = arr.findIndex(x => x.id === id)
          if (idx > -1) arr.splice(idx, 1)
        }
        removeFrom(this.general)
        removeFrom(this.allies)
        removeFrom(this.competitors)
      }
      unsavedIds.forEach(cleanForId)
    }

    // Finally, reset remaining markers' bucket assignments to baseline
    this.resetToBaseline()
  }

  /** Delete current item's POI markers from a given target group (used before moving items) */
  @action.bound
  async deleteCurrentBucketMarkersFromGroup (targetGroup) {
    if (!targetGroup) return
    const markersToMatch = this.temporaryMarkers || []
    if (markersToMatch.length === 0) return

    // Always reload saved markers to ensure we have the latest set
    await targetGroup.loadSavedMarkers()

    const EPS = 1e-4
    for (const savedMarker of targetGroup.savedMarkers) {
      const smLat = savedMarker.position && savedMarker.position.latitude
      const smLng = savedMarker.position && savedMarker.position.longitude
      if (smLat == null || smLng == null) continue

      const tempMarker = markersToMatch.find(m => {
        const mLat = m.position && m.position.latLng && m.position.latLng.lat
        const mLng = m.position && m.position.latLng && m.position.latLng.lng
        return typeof mLat === 'number' && typeof mLng === 'number' &&
          Math.abs(mLat - smLat) < EPS && Math.abs(mLng - smLng) < EPS
      })

      if (tempMarker) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.deleteMarker(savedMarker.id)
        } catch (err) {
          console.warn('Failed to delete marker before moving:', savedMarker.name, err)
        }
      }
    }
  }
}
export default PointOfInterest
